
import { BASE_COMPONENT, WizBaseComponent } from './../../../core/wiz-base-component';
import {WizTranslatableComponent} from '../../../core/wiz-translatable-component';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input,
          Renderer2, Output, EventEmitter, ViewChild, forwardRef} from '@angular/core';
import { WizActionObj } from '../../../../obj/navigation/wiz-action.obj';
import { WizImage } from '../../wiz-image/wiz-image';
import { Observable } from 'rxjs';
import { IWIZ_COMPONENT} from '../../../../interfaces/iwiz-component';
import { WIZ_EVENT_TYPE, WizStringUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';

@Component({
  selector: 'wiz-button',
  templateUrl: './wiz-button.html',
  styleUrls: ['./wiz-button.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush, // pas de listeneur permanent
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizButton) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizButton) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizButton) }
  ]
})
/** bouton de base + :
 * - gestion de la traduction
 * - gestion de l'icone / svg
 * - gestion du toggle / disabled
 */
export class WizButton extends WizTranslatableComponent {
  @ViewChild('innerButton', {read: ElementRef, static: true}) private innerButton: ElementRef<any>;
  @ViewChild('img', {read: WizImage, static: true}) public img: WizImage;

  /** Constructor with translation engine
   * @param cdref: ChangeDetectorRef
   * @param elementRef: ref au dom (point d'entrée)
   * @param renderer : rendu
   */
  constructor(protected cdref: ChangeDetectorRef,
              public elementRef: ElementRef,
              public  renderer: Renderer2
  ) {
    super(cdref, elementRef, renderer);
    this.checkStyle();

  }

  // emetteur pour pose de listener direct sur html (wizClick) = myMethod($event) => WizEvent
  @Output() wizClick: EventEmitter<WizEvent> = new EventEmitter<WizEvent>();
  // emetteur pour pose de listener direct sur html (wizClick) = myMethod($event) => WizEvent
  @Output() wizDoubleClick: EventEmitter<WizEvent> = new EventEmitter<WizEvent>();
  // méthode sur "onClick" en alimentation par prog (param = this)
  private callback: Function = null;

  /** dataset programmatic */
  private _data: WizActionObj; // alimentation par WizActionObj
  /**
   * Alimentation du WizButton depuis le wiz-component-manager
   * @param action : WizActionObj
   */
  public set data(action: WizActionObj) {
    this.setData(action);
  }
  public get data(): WizActionObj {
    return this._data;
  }

  /**
   * methodes getter/setter surchargeables de la data
   */
  protected setData(action: WizActionObj): void {
    this._data = action;

    this.wizLabel = action.label;
    this.setWizValue(action.value);
    this.wizIcon = action.imageSource;
    this.setWizDisabled(action.disabled);
    this.callback = action.callbackMethod;

    // console.log('btn width height', action.width, action.height);
    this.renderer.setStyle(this.elementRef.nativeElement, 'height', action.height);
    this.renderer.setStyle(this.elementRef.nativeElement, 'width', action.width);

    // paramètres supplémentaires
    // classe de style du bouton
    // source de l'icone (url / matDesign)
    // if (action.moreArgs) {
    //   Object.keys(action.moreArgs).forEach((key) => {
    //     if (key === 'class') {
    //       this.renderer.addClass(this.elementRef.nativeElement, action.moreArgs[key]);
    //     }

    //   });
    // }
    this.cdref.markForCheck();
  }
  protected getData(): WizActionObj {
    return this._data;
  }

  private _iconPlacement: 'right' | 'left' = 'left';

  /**
   * Emplacement de l'icon à droite ou à gauche du label
   */
  @Input() set iconPlacement(placement: 'right' | 'left') {
    this.setIconPlacement(placement);
  }
  get iconPlacement(): 'right' | 'left' {
    return this.getIconPlacement();
  }

  public get hasLabel(): boolean {
    return !WizStringUtils.isEmpty(this.wizLabel);
  }

  /**
   * Emplacement de l'icon à droite ou à gauche du label
   */
  public setIconPlacement(placement: 'right' | 'left'): void {
    this._iconPlacement = placement;
    this.cdref.markForCheck();
  }

  /**
   * Emplacement de l'icon à droite ou à gauche du label
   */
  public getIconPlacement(): 'right' | 'left' {
    return this._iconPlacement;
  }

  /** skinning */
  private _buttonModeClassName: string = 'buttonmode-class'; // class css pour mode bouton
  @Input()
  public set buttonModeClassName(value: string) {
    this.setButtonModeClassName(value);
  }
  /**
   * methode surchargeable de la classe css pour le mode bouton
   */
  protected setButtonModeClassName(value: string): void {
    this._buttonModeClassName = value;
    this.checkStyle(); // refresh du style
  }

  private _disabledClassName: string = 'disabled-class'; // class css pour skin "disabled"
  @Input()
  public set disabledClassName(value: string) {
    this.setDisabledClassName(value);
  }

  /**
   * methode surchargeable de la classe css pour le mode "disabled"
   */
  protected setDisabledClassName(value: string): void {
    this._disabledClassName = value;
    this.checkStyle(); // refresh du style
  }

  private _selectedClassName: string = 'selected-class'; // class css pour toggle = true
  @Input()
  public set selectedClassName(value: string) {
    this.setSelectedClassName(value);
  }

  /**
   * methode surchargeable de la classe css pour le mode toggle = true
   */
  protected setSelectedClassName(value: string): void {
    this._selectedClassName = value;
    this.checkStyle(); // refresh du style
  }

  /** mat-svg pour gestion simple */
  private _wizIcon: string = null;
  /** source, url de l'image du bouton */
  private _wizImage: Observable<string> = null;
  
  @Input()
  public set wizIcon(v: string) {
    this.setWizIcon(v);
  }
  public get wizIcon(): string {
    return this.getWizIcon();
  }

  /** injection icone material */
  private _wizMaterial: string = null;
  @Input()
  public set wizMaterial(v: string) {
    this._wizMaterial = v;
  }
  public get wizMaterial(): string {
    return this._wizMaterial;
  }


  @Input()
  public set wizImage(value: Observable<string>) {
    this._wizImage = value;
    this._wizIcon = null;
    this.cdref.markForCheck();
  }

  public get wizImage(): Observable<string> {
    return this.getWizImage();
  }

  /**
   * methodes getter/setter surchargeables du wizIcon
   */
  protected setWizIcon(value: string): void {
    this._wizImage = null;
    this._wizIcon = value;
    this.cdref.markForCheck();
  }

  protected getWizIcon(): string {
    return this._wizIcon;
  }
  /**
   * getter surchargeables du _wizImage
   */
  protected getWizImage(): Observable<string> {
    return this._wizImage;
  }

  /** application du style effectif (disabled, pointer, toggle.selected) */
  public checkStyle() {
    if (this.wizDisabled) { // inactif
      this.renderer.addClass(this.elementRef.nativeElement, this._disabledClassName);
      this.renderer.removeClass(this.elementRef.nativeElement, this._buttonModeClassName);
    } else { // actif
      this.renderer.removeClass(this.elementRef.nativeElement, this._disabledClassName);
      this.renderer.addClass(this.elementRef.nativeElement, this._buttonModeClassName);
      if (this._selected) { // gestion du selected en mode toggle
        this.renderer.addClass(this.elementRef.nativeElement, this._selectedClassName);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, this._selectedClassName);
      }
    }
  }

  /** Button click handler, pas de dispatch si disabled */
  public onClick(): void {
    if (!this.wizDisabled) {// Event not emitted if the button is disabled

      if (this._toggleMode) {// gestion du selected si toggle
        this.selected = !this._selected;
      }

      const event: WizEvent = new WizEvent(WIZ_EVENT_TYPE.CLICK, this); // event custom
      WizEventManager.dispatchEvent(this.elementRef, event); // sur le manager (accès .ts)
      this.wizClick.emit(event); // sur la source html pour écouteur direct

      if (this.callback) { // mode programmatic, appel de la callback
        this.callback(this);
      }
    }
  }

  /** idem mais canal 2xclic */
  public onDoubleClick(): void {
    if (!this.wizDisabled) {// Event not emitted if the button is disabled
      const event: WizEvent = new WizEvent(WIZ_EVENT_TYPE.DOUBLE_CLICK, this); // event custom
      WizEventManager.dispatchEvent(this.elementRef, event); // sur le manager (accès .ts)
      this.wizDoubleClick.emit(event); // sur la source html pour bubbling
      this.innerButton.nativeElement.blur();
    }
  }

  /** event transmission when the input get the focus */
  public wizOnFocus(): void {
    const event: WizEvent = new WizEvent(WIZ_EVENT_TYPE.FOCUS_IN, this); // event custom
    WizEventManager.dispatchEvent(this.elementRef, event); // sur le manager (accès .ts)
  }

  /** mode toggle / ou pas */
  protected _toggleMode: boolean = false;
  @Input() public set toggleMode(v: boolean) {
    this.setToggleMode(v);
  }
  public get toggleMode(): boolean {
    return this.getToggleMode();
  }

  /**
   * methodes getter/setter surchargeables du toggle mode
   */
  protected setToggleMode(value: boolean): void {
    this._toggleMode = value;
    this.cdref.markForCheck();
  }
  protected getToggleMode(): boolean {
    return this._toggleMode;
  }

  /** bouton actif (sélectionné) dans le cas du mode toggle / dans une ButtonBar  */
  protected _selected: boolean = false;
  @Input() set selected(v: boolean) {
    this.setSelected(v);
  }
  get selected(): boolean {
    return this.getSelected();
  }

  /**
   * methodes getter/setter surchargeables du selected
   */
  protected setSelected(value: boolean): void {
    this._selected = value;
    this.checkStyle(); // refresh du style
    this.cdref.markForCheck();
  }

  protected getSelected(): boolean {
    return this._selected;
  }
}
